import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useLanguage } from '../../context/LanguageContext';
import { GitCompare, Calculator, Trash2 } from 'lucide-react';

const ScenarioComparison = () => {
  const { language } = useLanguage();
  
  const [scenarioA, setScenarioA] = useState({
    name: 'Scenario A',
    area: 50,
    volume: 7500,
    pricePerM3: 580,
    growthRate: 5.5
  });
  
  const [scenarioB, setScenarioB] = useState({
    name: 'Scenario B',
    area: 75,
    volume: 10000,
    pricePerM3: 520,
    growthRate: 4.8
  });
  
  const [showResults, setShowResults] = useState(false);

  const content = {
    sv: {
      title: 'Jämför skogsscenarier',
      description: 'Jämför två skogsscenarier sida vid sida för att analysera skillnader i värde och potential.',
      scenarioName: 'Scenarionamn',
      area: 'Areal (ha)',
      volume: 'Volym (m³sk)',
      pricePerM3: 'Pris (SEK/m³sk)',
      growthRate: 'Tillväxt (%)',
      compare: 'Jämför scenarier',
      reset: 'Återställ',
      results: 'Jämförelseresultat',
      totalValue: 'Totalt värde',
      valuePerHa: 'Värde per ha',
      volumePerHa: 'Volym per ha',
      annualGrowthValue: 'Årlig tillväxtvärde',
      difference: 'Skillnad',
      higher: 'högre',
      lower: 'lägre'
    },
    de: {
      title: 'Waldszenarien vergleichen',
      description: 'Vergleichen Sie zwei Waldszenarien nebeneinander, um Unterschiede in Wert und Potenzial zu analysieren.',
      scenarioName: 'Szenarioname',
      area: 'Fläche (ha)',
      volume: 'Volumen (m³)',
      pricePerM3: 'Preis (SEK/m³)',
      growthRate: 'Zuwachs (%)',
      compare: 'Szenarien vergleichen',
      reset: 'Zurücksetzen',
      results: 'Vergleichsergebnisse',
      totalValue: 'Gesamtwert',
      valuePerHa: 'Wert pro ha',
      volumePerHa: 'Volumen pro ha',
      annualGrowthValue: 'Jährlicher Zuwachswert',
      difference: 'Differenz',
      higher: 'höher',
      lower: 'niedriger'
    },
    en: {
      title: 'Compare forest scenarios',
      description: 'Compare two forest scenarios side-by-side to analyze differences in value and potential.',
      scenarioName: 'Scenario name',
      area: 'Area (ha)',
      volume: 'Volume (m³)',
      pricePerM3: 'Price (SEK/m³)',
      growthRate: 'Growth (%)',
      compare: 'Compare scenarios',
      reset: 'Reset',
      results: 'Comparison results',
      totalValue: 'Total value',
      valuePerHa: 'Value per ha',
      volumePerHa: 'Volume per ha',
      annualGrowthValue: 'Annual growth value',
      difference: 'Difference',
      higher: 'higher',
      lower: 'lower'
    },
    se: {
      title: 'Buohtastahte meahcceskenáriojaid',
      description: 'Buohtastahte guokte meahcceskenário bálddalagaid analyseremii.',
      scenarioName: 'Skenárionamma',
      area: 'Areála (ha)',
      volume: 'Voluma (m³)',
      pricePerM3: 'Haddi (SEK/m³)',
      growthRate: 'Šaddama (%)',
      compare: 'Buohtastahte',
      reset: 'Nulles',
      results: 'Buohtastahttinbohtosat'
    }
  };

  const c = content[language] || content.sv;

  const calculateResults = (scenario) => {
    const totalValue = scenario.volume * scenario.pricePerM3;
    const valuePerHa = totalValue / scenario.area;
    const volumePerHa = scenario.volume / scenario.area;
    const annualGrowthValue = (scenario.area * volumePerHa * (scenario.growthRate / 100)) * scenario.pricePerM3;
    
    return {
      totalValue,
      valuePerHa,
      volumePerHa,
      annualGrowthValue
    };
  };

  const handleCompare = () => {
    setShowResults(true);
  };

  const handleReset = () => {
    setShowResults(false);
    setScenarioA({ name: 'Scenario A', area: 50, volume: 7500, pricePerM3: 580, growthRate: 5.5 });
    setScenarioB({ name: 'Scenario B', area: 75, volume: 10000, pricePerM3: 520, growthRate: 4.8 });
  };

  const resultsA = calculateResults(scenarioA);
  const resultsB = calculateResults(scenarioB);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('sv-SE', { style: 'decimal', maximumFractionDigits: 0 }).format(value) + ' kr';
  };

  const getDifferenceClass = (a, b) => {
    if (a > b) return 'text-green-600';
    if (a < b) return 'text-red-600';
    return 'text-gray-600';
  };

  const ScenarioInput = ({ scenario, setScenario, label }) => (
    <div className="space-y-3 p-4 bg-forest-50 rounded-lg">
      <h4 className="font-semibold text-forest-800">{label}</h4>
      <div className="space-y-2">
        <div className="space-y-1">
          <Label className="text-xs">{c.scenarioName}</Label>
          <Input
            value={scenario.name}
            onChange={(e) => setScenario({ ...scenario, name: e.target.value })}
            placeholder={label}
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label className="text-xs">{c.area}</Label>
            <Input
              type="number"
              value={scenario.area}
              onChange={(e) => setScenario({ ...scenario, area: Number(e.target.value) })}
              min={1}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{c.volume}</Label>
            <Input
              type="number"
              value={scenario.volume}
              onChange={(e) => setScenario({ ...scenario, volume: Number(e.target.value) })}
              min={1}
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label className="text-xs">{c.pricePerM3}</Label>
            <Input
              type="number"
              value={scenario.pricePerM3}
              onChange={(e) => setScenario({ ...scenario, pricePerM3: Number(e.target.value) })}
              min={1}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{c.growthRate}</Label>
            <Input
              type="number"
              step="0.1"
              value={scenario.growthRate}
              onChange={(e) => setScenario({ ...scenario, growthRate: Number(e.target.value) })}
              min={0}
            />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Card className="w-full" data-testid="scenario-comparison">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-forest-700">
          <GitCompare className="h-5 w-5" />
          {c.title}
        </CardTitle>
        <CardDescription>{c.description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ScenarioInput 
            scenario={scenarioA} 
            setScenario={setScenarioA} 
            label={scenarioA.name}
          />
          <ScenarioInput 
            scenario={scenarioB} 
            setScenario={setScenarioB} 
            label={scenarioB.name}
          />
        </div>

        <div className="flex gap-2">
          <Button 
            onClick={handleCompare} 
            className="flex-1 bg-forest-700 hover:bg-forest-800"
            data-testid="compare-btn"
          >
            <Calculator className="mr-2 h-4 w-4" />
            {c.compare}
          </Button>
          <Button 
            variant="outline" 
            onClick={handleReset}
            data-testid="reset-btn"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            {c.reset}
          </Button>
        </div>

        {showResults && (
          <div className="mt-4 p-4 bg-white rounded-lg border" data-testid="comparison-results">
            <h4 className="font-semibold text-forest-800 mb-4">{c.results}</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="py-2 px-3 text-left font-medium"></th>
                    <th className="py-2 px-3 text-right font-medium text-forest-700">{scenarioA.name}</th>
                    <th className="py-2 px-3 text-right font-medium text-forest-700">{scenarioB.name}</th>
                    <th className="py-2 px-3 text-right font-medium text-gray-500">{c.difference}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="py-2 px-3 font-medium">{c.totalValue}</td>
                    <td className={`py-2 px-3 text-right font-mono ${getDifferenceClass(resultsA.totalValue, resultsB.totalValue)}`}>
                      {formatCurrency(resultsA.totalValue)}
                    </td>
                    <td className={`py-2 px-3 text-right font-mono ${getDifferenceClass(resultsB.totalValue, resultsA.totalValue)}`}>
                      {formatCurrency(resultsB.totalValue)}
                    </td>
                    <td className="py-2 px-3 text-right font-mono text-gray-500">
                      {formatCurrency(Math.abs(resultsA.totalValue - resultsB.totalValue))}
                    </td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 px-3 font-medium">{c.valuePerHa}</td>
                    <td className={`py-2 px-3 text-right font-mono ${getDifferenceClass(resultsA.valuePerHa, resultsB.valuePerHa)}`}>
                      {formatCurrency(resultsA.valuePerHa)}
                    </td>
                    <td className={`py-2 px-3 text-right font-mono ${getDifferenceClass(resultsB.valuePerHa, resultsA.valuePerHa)}`}>
                      {formatCurrency(resultsB.valuePerHa)}
                    </td>
                    <td className="py-2 px-3 text-right font-mono text-gray-500">
                      {formatCurrency(Math.abs(resultsA.valuePerHa - resultsB.valuePerHa))}
                    </td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 px-3 font-medium">{c.volumePerHa}</td>
                    <td className={`py-2 px-3 text-right font-mono ${getDifferenceClass(resultsA.volumePerHa, resultsB.volumePerHa)}`}>
                      {Math.round(resultsA.volumePerHa)} m³sk/ha
                    </td>
                    <td className={`py-2 px-3 text-right font-mono ${getDifferenceClass(resultsB.volumePerHa, resultsA.volumePerHa)}`}>
                      {Math.round(resultsB.volumePerHa)} m³sk/ha
                    </td>
                    <td className="py-2 px-3 text-right font-mono text-gray-500">
                      {Math.round(Math.abs(resultsA.volumePerHa - resultsB.volumePerHa))} m³sk/ha
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2 px-3 font-medium">{c.annualGrowthValue}</td>
                    <td className={`py-2 px-3 text-right font-mono ${getDifferenceClass(resultsA.annualGrowthValue, resultsB.annualGrowthValue)}`}>
                      {formatCurrency(resultsA.annualGrowthValue)}/år
                    </td>
                    <td className={`py-2 px-3 text-right font-mono ${getDifferenceClass(resultsB.annualGrowthValue, resultsA.annualGrowthValue)}`}>
                      {formatCurrency(resultsB.annualGrowthValue)}/år
                    </td>
                    <td className="py-2 px-3 text-right font-mono text-gray-500">
                      {formatCurrency(Math.abs(resultsA.annualGrowthValue - resultsB.annualGrowthValue))}/år
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ScenarioComparison;
